import fetch from "node-fetch";

const SLACK_WEBHOOK_URL =
  process.env.NODE_ENV === "production"
    ? process.env.PRODU_SLACK_WEBHOOK_URL
    : process.env.SLACK_WEBHOOK_URL;

export const LOG_LEVELS = {
  ERROR: "ERROR",
  WARN: "WARN",
  INFO: "INFO",
  DEBUG: "DEBUG",
};

const THROTTLE_DURATION = 5 * 60 * 1000;
const errorThrottleMap = new Map();

const getRequestContext = (req) => {
  if (!req) {
    return {
      path: typeof window !== "undefined" ? window.location.pathname : "/",
      method: "UNKNOWN",
      url: typeof window !== "undefined" ? window.location.href : "/",
      ip: "unknown",
      userAgent:
        typeof window !== "undefined" ? window.navigator.userAgent : "unknown",
      timestamp: new Date().toISOString(),
    };
  }

  const protocol = req.headers?.["x-forwarded-proto"] || "http";
  const host = req.headers?.host || "localhost:3000";
  const pathname = req.url || req.path || "/";
  const fullUrl = `${protocol}://${host}${pathname}`;

  return {
    path: pathname,
    method: req.method || "UNKNOWN",
    url: fullUrl,
    ip:
      req.headers?.["x-forwarded-for"] ||
      req.connection?.remoteAddress ||
      "unknown",
    userAgent: req.headers?.["user-agent"] || "unknown",
    timestamp: new Date().toISOString(),
  };
};

const formatErrorMessage = (error, context, level = LOG_LEVELS.ERROR) => {
  let errorObj;

  if (error instanceof Error) {
    errorObj = error;
  } else if (typeof error === "object" && error !== null) {
    // Handle plain objects by creating a proper Error
    const message = error.message || error.toString() || "Unknown error";
    errorObj = new Error(message);
    errorObj.name = error.name || "Error";
    // Copy relevant properties but avoid circular references
    if (error.stack) errorObj.stack = error.stack;
    if (error.code) errorObj.code = error.code;
  } else {
    // Handle strings, numbers, etc.
    const message = String(error || "Unknown error");
    errorObj = new Error(message);
  }

  const errorDetails = {
    name: errorObj.name || "Error",
    message: errorObj.message || "Unknown error",
    stack: errorObj.stack || new Error().stack,
  };

  return {
    blocks: [
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*${level}*: ${errorDetails.name} - ${errorDetails.message}`,
        },
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*Context:*\n\`\`\`${JSON.stringify(context, null, 2)}\`\`\``,
        },
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*Stack Trace:*\n\`\`\`${errorDetails.stack || "No stack trace available"}\`\`\``,
        },
      },
    ],
  };
};

const isThrottled = (error) => {
  const key = `${error.name}:${error.message}:${error.stack}`;
  const lastLogged = errorThrottleMap.get(key);
  const now = Date.now();

  if (lastLogged && now - lastLogged < THROTTLE_DURATION) {
    return true;
  }

  errorThrottleMap.set(key, now);
  return false;
};

export const logError = async (error, req, level = LOG_LEVELS.ERROR) => {
  try {
    let errorObj;

    if (error instanceof Error) {
      errorObj = error;
    } else if (typeof error === "object" && error !== null) {
      // Handle plain objects by creating a proper Error
      const message = error.message || error.toString() || "Unknown error";
      errorObj = new Error(message);
      errorObj.name = error.name || "Error";
      // Copy relevant properties but avoid circular references
      if (error.stack) errorObj.stack = error.stack;
      if (error.code) errorObj.code = error.code;
    } else {
      // Handle strings, numbers, etc.
      const message = String(error || "Unknown error");
      errorObj = new Error(message);
    }

    if (isThrottled(errorObj)) {
      return;
    }

    const context = getRequestContext(req);

    const baseUrl =
      typeof window !== "undefined"
        ? window.location.origin
        : process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

    const response = await fetch(`${baseUrl}/api/logger`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        error: {
          name: errorObj.name || "Error",
          message: errorObj.message || "Unknown error",
          stack: errorObj.stack || new Error().stack,
        },
        req: context,
        level,
      }),
    });

    if (!response.ok && process.env.NODE_ENV === "development") {
      // Only log in development
      console.error("Error logging failed:", await response.text());
    }
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      // Only log in development
      console.error("Logger error:", err);
    }
  }
};

export default {
  error: (error, req) => logError(error, req, LOG_LEVELS.ERROR),
  warn: (error, req) => logError(error, req, LOG_LEVELS.WARN),
  info: (error, req) => logError(error, req, LOG_LEVELS.INFO),
  debug: (error, req) => logError(error, req, LOG_LEVELS.DEBUG),
};
